// src/hooks/usePricing.js
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useData } from "../context/DataContext";
import { getColorsByProductCode } from "../constants/colors";
import { getFontsByProductCode } from "../constants/fonts";
import { getFinishByProductCode } from "../constants/finishes";
import { getThicknessUpcharge } from "../constants/thicknessUpcharges";

const PricingContext = createContext();

export function PricingProvider({ children }) {
  const { lines, quantity, selectedProduct, updateLine } = useData();
  const [totalPrice, setTotalPrice] = useState(0);

  const calculateLinePrice = useCallback(
    (line) => {

      const text = line.text?.trim();
      if (!text || !selectedProduct) {
        return 0;
      }

      const size = selectedProduct.sizes?.find((s) => s.code === line.size);
      if (!size?.basePrice) {
        return 0;
      }

      let basePrice = size.basePrice;

      if (["gold_213", "silver_801"].includes(line.color)) {
        basePrice *= 1.25;
      }

            // === THICKNESS UPCHARGE (flat dollar amount) ===
      let thicknessUpcharge = 0;
      if (line.thickness && line.size) {
        thicknessUpcharge = getThicknessUpcharge(
          selectedProduct.code,
          line.thickness,
          line.size
        );
        if (thicknessUpcharge > 0) {
          basePrice += thicknessUpcharge;
        }
      }

      // Calculate color upcharge

      let colorUpcharge = 1.0;
      if (line.color) {
        const colorOptions = getColorsByProductCode(selectedProduct.code) || [];
        const selectedColor = colorOptions.find(
          (c) => c.mivaname === line.color
        );
        colorUpcharge = selectedColor?.upcharge || 1.0;
        basePrice = basePrice * colorUpcharge;
      }

      // Calculate FontUpcharge
      let fontUpcharge = 1.0;
      if (line.font) {
        const fontOptions = getFontsByProductCode(selectedProduct.code) || [];
        const selectedFont = fontOptions.find((f) => f.code === line.font);
        fontUpcharge = selectedFont?.upcharge || 1.0;
        basePrice = basePrice * fontUpcharge;
      }

      // Calculate finish upcharge
      if (line.finish) {
        const finishes = getFinishByProductCode(selectedProduct.code) || [];
        const finish = finishes.find((f) => f.code === line.finish);
        if (finish?.upcharge && finish.upcharge !== 1.0) {
          basePrice *= finish.upcharge;
        }
      }

      // Calculate letter case factor

      const lowercaseFactor = [
        "BL1000",
        "BL2000",
        "BL2200",
        "BL5000",
        "ML1000",
      ].includes(selectedProduct.code)
        ? 0.9
        : [
            "BL31000",
            "BL32000",
            "BL33000",
            "BL40100",
            "BL40200",
            "BL70100",
            "PFL100",
          ].includes(selectedProduct.code)
        ? 0.75
        : 1;

      const uppercase = (text.match(/[A-Z]/g) || []).length;
      const lowercase = (text.match(/[a-z]/g) || []).length;
      const punctuation = text.length - uppercase - lowercase;
      let finalPrice =
        uppercase * basePrice +
        lowercase * lowercaseFactor * basePrice +
        punctuation * (1 / 3) * basePrice;

      if (selectedProduct.priceFactor) {
        finalPrice = finalPrice * selectedProduct.priceFactor;
      }

      return finalPrice;
    },
    [selectedProduct]
  );

  const updatePricing = useCallback(() => {

    if (!lines.length || !selectedProduct) {
      setTotalPrice(0);
      return;
    }

    let subtotal = 0;

    lines.forEach((line) => {
      const linePrice = calculateLinePrice(line);
      subtotal += linePrice;

      if (line.price !== linePrice) {
        updateLine(line.id, { price: linePrice });
      }
    });

    const total = Number((subtotal * quantity).toFixed(2));
    setTotalPrice(total);
  }, [lines, selectedProduct, quantity, updateLine, calculateLinePrice]);

  useEffect(() => {
    updatePricing();
  }, [updatePricing]);

  const formatPrice = (price) => `$${Number(price).toFixed(2)}`;

  return (
    <PricingContext.Provider
      value={{
        totalPrice,
        formatPrice,
        updatePricing,
        calculateLinePrice,
      }}
    >
      {children}
    </PricingContext.Provider>
  );
}

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context)
    throw new Error("usePricing must be used within PricingProvider");
  return context;
};
